import axios, { type AxiosInstance } from 'axios'
import { createPasskeyClient } from '@valiant/ui-shared'
import { environment } from './environment'
export class Auth {
  private readonly axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: environment.API_URL,
      headers: {
        Accept: 'application/json',
      },
    })

    this.axiosInstance.interceptors.request.use(
      (config) => {
        console.debug(`LOGIN request: ${config.method} ${config.url}`)
        return config
      },
      (error) => {
        console.error('Login request failed')
        const errorMessage = readAxiosError(error)
        return Promise.reject(new Error(errorMessage))
      },
    )

    this.axiosInstance.interceptors.response.use(
      (response) => {
        console.debug(`LOGIN response: ${response.status} ${response.statusText}`)
        return response
      },
      (error) => {
        console.error('Login response failed')
        const errorMessage = readAxiosError(error)
        return Promise.reject(new Error(errorMessage))
      },
    )
  }

  async login(
    username: string,
    password: string,
    secret?: string,
  ): Promise<{ token: string; refreshToken: string }> {
    const response = await this.axiosInstance.post<{
      token: string
      refreshToken: string
    }>('/authentication/login', { username, password, secret })
    return response.data
  }

  async adminLogin(
    username: string,
    password: string,
    secret?: string,
  ): Promise<{ token: string; refreshToken: string }> {
    const response = await this.axiosInstance.post<{
      token: string
      refreshToken: string
    }>('/authentication/admin/login', { username, password, secret })
    return response.data
  }

  async impersonate(
    adminToken: string,
    email: string,
  ): Promise<{ token: string; refreshToken: string }> {
    const response = await this.axiosInstance.post<{
      token: string
      refreshToken: string
    }>(
      '/authentication/admin/impersonate',
      { email },
      { headers: { Authorization: `Bearer ${adminToken}` } },
    )
    return response.data
  }

  isJwtStale(jwt: string): boolean {
    const payload = JSON.parse(atob(jwt.split('.')[1]))
    const expiration = payload.exp * 1000
    return Date.now() >= expiration
  }

  async refresh(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
    const response = await this.axiosInstance.post<{
      token: string
      refreshToken: string
    }>('/authentication/refreshtoken', { refreshToken })
    return response.data
  }

  async resetPassword(password: string, token: string): Promise<void> {
    await this.axiosInstance.post('/authentication/resetpassword', { password, token })
  }

  async requestMFA(username: string, password: string): Promise<string> {
    const response = await this.axiosInstance.post<{ secret: string }>(
      '/authentication/request-mfa',
      { username, password },
    )
    return response.data.secret
  }

  async setupMFA(username: string, password: string, secret: string): Promise<void> {
    await this.axiosInstance.post('/authentication/setup-mfa', {
      username,
      password,
      secret,
    })
  }

  private readonly passkeys = createPasskeyClient(environment.API_URL)
  passkeysEnabled = () => this.passkeys.enabled()
  passkeyLoginAvailable = (username: string, password: string) => this.passkeys.loginAvailable(username, password)
  loginWithPasskey = (username: string, password: string) => this.passkeys.login(username, password)

}

function readAxiosError(error: {
  response?: { data?: unknown }
  message?: string
}): string {
  const data = error.response?.data
  if (typeof data === 'string' && data.length > 0) return data
  if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string') {
    return data.error
  }
  return error.message ?? 'Unknown error occurred'
}
